import React from 'react';
import { Lock, Unlock, ChevronDown, ChevronRight } from 'lucide-react';

import Input from '../ui/Input';
import usePersistedCollapse from '../../hooks/usePersistedCollapse';

const SIDES = [
  { key: 'top', label: 'Top' },
  { key: 'right', label: 'Right' },
  { key: 'bottom', label: 'Bottom' },
  { key: 'left', label: 'Left' },
];

const clampToNonNegative = (value) => Math.max(0, Number(value) || 0);

// Locked (default): one value applied to all four sides.
// Unlocked: independent top/right/bottom/left, same layout as PageMarginsPanel.
export default function CellSettingsPanel({ cellPadding, onCellPaddingChange }) {
  const [collapsed, setCollapsed] = usePersistedCollapse('cell-padding');
  const { locked, top } = cellPadding;

  const handleLockedValueChange = (value) => {
    const next = clampToNonNegative(value);
    onCellPaddingChange({ locked: true, top: next, right: next, bottom: next, left: next });
  };

  const handleSideChange = (side, value) => {
    onCellPaddingChange({ ...cellPadding, [side]: clampToNonNegative(value) });
  };

  const toggleLocked = () => {
    if (locked) {
      onCellPaddingChange({ ...cellPadding, locked: false });
    } else {
      // Re-lock: collapse all sides to the current top value.
      onCellPaddingChange({ locked: true, top, right: top, bottom: top, left: top });
    }
  };

  return (
    <div className="ss-print__panel">
      <div className="ss-print__panel-header">
        <h3 className="ss-print__panel-title">Cell Padding</h3>
        <div className="ss-print__panel-header-actions">
          {!collapsed && (
            <button
              type="button"
              className="ss-print__lock-toggle"
              onClick={toggleLocked}
              aria-pressed={locked}
              aria-label={locked ? 'Unlock sides to set independently' : 'Lock all sides to the same value'}
              title={locked ? 'All sides linked — click to set independently' : 'Sides independent — click to link'}
            >
              {locked ? <Lock size={16} aria-hidden="true" /> : <Unlock size={16} aria-hidden="true" />}
            </button>
          )}
          <button
            type="button"
            className="ss-print__lock-toggle"
            onClick={() => setCollapsed(!collapsed)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand cell padding panel' : 'Collapse cell padding panel'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {!collapsed && (
        locked ? (
          <Input
            label="All sides"
            type="number"
            min={0}
            step={0.01}
            value={top}
            onChange={(e) => handleLockedValueChange(e.target.value)}
          />
        ) : (
          <div className="ss-print__panel-row ss-print__panel-row--wrap">
            {SIDES.map(({ key, label }) => (
              <Input
                key={key}
                label={label}
                type="number"
                min={0}
                step={0.01}
                value={cellPadding[key]}
                onChange={(e) => handleSideChange(key, e.target.value)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}
